(function () {
  'use strict';

  const MISSING_VALUE = '[Wert fehlt]';
  let submissions = new WeakMap();
  window.addEventListener('pageshow', event => {
    if (event.persisted) submissions = new WeakMap();
  });

  function freeze(value) {
    if (value && typeof value === 'object') {
      Object.values(value).forEach(freeze);
      Object.freeze(value);
    }
    return value;
  }

  // The snapshot is assembled synchronously, after Alpine has settled. Nothing
  // below capture or submit may consult a live configurator store again.
  function createSnapshot({ config, summary, nightMode, scene, currentStep, deliveryText, priceVariants, configurator = window.maisonBedConfigurator }) {
    if (!config || !summary || !configurator || typeof configurator.getImages !== 'function' ||
        typeof configurator.getImageUrl !== 'function' || !configurator.variants) {
      throw new Error('The bed configuration is unavailable.');
    }
    if (typeof window.deliveryProperties?.assign !== 'function') {
      throw new Error('Delivery properties are unavailable.');
    }

    const savedConfig = JSON.parse(JSON.stringify(config));
    const totalPrice = Number(summary.totalPrice);
    if (!Number.isSafeInteger(totalPrice) || totalPrice < 0) {
      const error = new Error('The bed price is invalid.');
      error.code = 'pricing';
      throw error;
    }
    const variants = priceVariants ?? configurator.variants;
    let resolved;
    if (window.MaisonBedPrices?.resolve) {
      resolved = window.MaisonBedPrices.resolve(totalPrice, variants);
    } else {
      // The optional catalog asset may fail to load. Only an exact Liquid
      // match can be used; a cheaper variant would contradict the display.
      resolved = {
        variantId: Object.hasOwn(variants, totalPrice) ? Number(variants[totalPrice]) : null,
        variantPrice: totalPrice, matchType: 'exact',
      };
    }
    const { variantId, variantPrice, matchType: priceMatch } = resolved;
    // Also enforce this at the snapshot boundary if an older cached resolver
    // asset is present. No capture, tracking or submission precedes this check.
    if (!Number.isSafeInteger(variantId) || variantId <= 0 || variantPrice !== totalPrice || priceMatch !== 'exact') {
      const error = new Error('No sellable variant exactly matches this bed price.');
      error.code = 'pricing';
      throw error;
    }

    const properties = Object.fromEntries(Object.entries(summary.properties).map(
      ([key, value]) => [key.startsWith('_') ? key : `_${key}`, value],
    ));
    summary.steps.forEach(step => {
      let itemCount = 0;
      step.items.forEach(({ label }) => {
        if (label.includes(':')) {
          const [key, ...valueParts] = label.split(':');
          properties[key] = valueParts.join(':').trim();
        } else {
          const suffix = itemCount ? ` ${itemCount + 1}` : '';
          properties[`${step.title}${suffix}`] = label;
          itemCount++;
        }
      });
    });
    properties._bedconfig = JSON.stringify(savedConfig);
    window.deliveryProperties.assign(properties, deliveryText);

    // Normalize BEFORE cloning: JSON copying raw properties loses undefined
    // entries, which must retain the existing missing-value placeholder.
    const finalProperties = Object.fromEntries(Object.entries(properties)
      .filter(([, value]) => value !== null)
      .map(([key, value]) => [key, value === undefined || value === '' ? MISSING_VALUE : String(value)]));

    const images = configurator.getImages(savedConfig, nightMode, currentStep, scene);
    if (!Array.isArray(images) || images.length === 0) throw new Error('The bed image has no layers.');
    const layers = images.map(image => {
      const filename = typeof image === 'object' && image !== null ? image.filename : image;
      if (typeof filename !== 'string' || !filename.trim()) throw new Error('A bed image layer is missing.');
      const url = configurator.getImageUrl(filename);
      if (typeof url !== 'string' || !url.trim()) throw new Error('A bed image URL is missing.');
      return {
        url, blendMode: (typeof image === 'object' && image.blendMode) || 'normal',
        ...(image.wallBacking === true ? { wallBacking: true } : {}),
      };
    });

    return freeze({ config: savedConfig, properties: finalProperties, totalPrice, variantId, variantPrice, priceMatch, nightMode, scene: typeof scene === 'string' ? scene : '', currentStep, layers });
  }

  function abortError(signal) {
    if (signal?.reason) return signal.reason;
    const error = new Error('Bed cart preparation was cancelled.');
    error.name = 'AbortError';
    return error;
  }

  function checkActive(signal) {
    if (signal?.aborted) throw abortError(signal);
  }

  function abortable(promise, signal) {
    return new Promise((resolve, reject) => {
      let settled = false;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        signal.removeEventListener('abort', onAbort);
        callback(value);
      };
      const onAbort = () => finish(reject, abortError(signal));
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
      Promise.resolve(promise).then(value => finish(resolve, value), error => finish(reject, error));
    });
  }

  function loadLayer(image, url, signal) {
    return new Promise((resolve, reject) => {
      let settled = false;
      let decoding = false;
      const finish = error => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        signal.removeEventListener('abort', onAbort);
        error ? reject(error) : resolve();
      };
      const onAbort = () => finish(abortError(signal));
      const onError = () => finish(new Error('A bed image layer could not be loaded.'));
      const hasDimensions = () => image.naturalWidth > 0 && image.naturalHeight > 0;
      const onLoad = () => {
        if (settled || decoding) return;
        if (!hasDimensions()) return finish(new Error('A bed image layer has invalid dimensions.'));
        if (typeof image.decode !== 'function') return finish(new Error('Image decoding is unavailable.'));
        decoding = true;
        Promise.resolve().then(() => {
          checkActive(signal);
          return image.decode();
        }).then(() => {
          if (!hasDimensions()) return finish(new Error('A bed image layer has invalid dimensions.'));
          finish();
        }, error => finish(error));
      };

      image.addEventListener('load', onLoad);
      image.addEventListener('error', onError);
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) return onAbort();
      image.crossOrigin = 'anonymous';
      image.loading = 'eager';
      image.src = url;
      // A cached image can already be complete before its load event runs.
      if (image.complete) onLoad();
    });
  }

  async function capture(snapshot, { referenceNode, scale = 1, signal } = {}) {
    checkActive(signal);
    if (!referenceNode || typeof referenceNode.getBoundingClientRect !== 'function') {
      throw new Error('The bed preview is unavailable.');
    }
    if (typeof window.html2canvas !== 'function') throw new Error('Image capture is unavailable.');
    if (!Array.isArray(snapshot?.layers) || snapshot.layers.length === 0) throw new Error('The bed image has no layers.');
    snapshot.layers.forEach(layer => {
      if (typeof layer?.url !== 'string' || !layer.url.trim()) throw new Error('A bed image layer is missing.');
    });
    const { width, height } = referenceNode.getBoundingClientRect();
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0 ||
        !Number.isFinite(scale) || scale <= 0) {
      throw new Error('The bed preview has invalid dimensions.');
    }
    const doc = referenceNode.ownerDocument || document;
    if (!doc.body) throw new Error('The bed preview document is unavailable.');

    const controller = new AbortController();
    const onAbort = () => controller.abort(abortError(signal));
    signal?.addEventListener('abort', onAbort, { once: true });
    const images = [];
    const cloneFrames = new Set();
    const wrapper = doc.createElement('div');
    wrapper.setAttribute('data-bed-cart-capture', '');
    wrapper.setAttribute('aria-hidden', 'true');
    Object.assign(wrapper.style, {
      position: 'fixed', left: '-100000px', top: '0', width: `${width}px`,
      height: `${height}px`, pointerEvents: 'none',
    });
    const stack = doc.createElement('div');
    Object.assign(stack.style, {
      position: 'relative', width: `${width}px`, height: `${height}px`,
      overflow: 'hidden', zIndex: '0',
    });
    wrapper.appendChild(stack);

    try {
      checkActive(signal);
      doc.body.appendChild(wrapper);
      const pendingLayers = snapshot.layers.map((layer, index) => {
        const image = doc.createElement('img');
        image.alt = '';
        Object.assign(image.style, {
          display: 'block', width: '100%', maxWidth: '100%', height: 'auto',
          position: index === 0 ? 'relative' : 'absolute',
          ...(index === 0 ? {} : { inset: '0' }),
          mixBlendMode: layer.blendMode || 'normal',
        });
        images.push(image);
        if (layer.wallBacking) {
          // Match the preview's clipped wall extension. Use an overflow wrapper
          // because html2canvas does not reliably capture CSS clip-path.
          const wall = doc.createElement('div');
          Object.assign(wall.style, {
            position: 'absolute', top: '0', left: '0', width: '100%',
            height: '30%', overflow: 'hidden',
          });
          Object.assign(image.style, {
            position: 'absolute', inset: '0', transform: `translateY(${1000 / 709}%)`,
          });
          wall.appendChild(image);
          stack.appendChild(wall);
        } else {
          stack.appendChild(image);
        }
        return loadLayer(image, layer.url, controller.signal);
      });
      await Promise.all(pendingLayers);
      checkActive(controller.signal);

      const renderOptions = {
        allowTaint: true,
        useCORS: true,
        imageTimeout: 0,
        logging: false,
        scale,
        width,
        height,
        removeContainer: true,
        // This stack is fully styled inline. Excluding the rest of the page
        // prevents unrelated storefront images/fonts from delaying its clone.
        ignoreElements: element => !wrapper.contains(element) && !element.contains(wrapper),
        onclone(clonedDocument) {
          // html2canvas cannot itself be cancelled. Own its clone when exposed
          // so a late clone callback cannot leave a frame behind after abort.
          const frame = clonedDocument.defaultView?.frameElement;
          if (frame) cloneFrames.add(frame);
          if (controller.signal.aborted) {
            frame?.remove();
            throw abortError(controller.signal);
          }
        },
      };
      // The fork creates its iframe synchronously, before onclone can run.
      // Record only frames added by this invocation, including if it throws.
      const previousFrames = new Set(doc.querySelectorAll('iframe.html2canvas-container'));
      let rendering;
      try {
        rendering = window.html2canvas(stack, renderOptions);
      } finally {
        doc.querySelectorAll('iframe.html2canvas-container').forEach(frame => {
          if (!previousFrames.has(frame)) cloneFrames.add(frame);
        });
      }
      const canvas = await abortable(rendering, controller.signal);
      checkActive(controller.signal);
      if (!canvas || !Number.isFinite(canvas.width) || !Number.isFinite(canvas.height) ||
          canvas.width <= 0 || canvas.height <= 0 || typeof canvas.toBlob !== 'function') {
        throw new Error('The bed image could not be rendered.');
      }
      const blob = await abortable(new Promise((resolve, reject) => {
        try {
          canvas.toBlob(resolve, 'image/jpeg', 0.8);
        } catch (error) {
          reject(error);
        }
      }), controller.signal);
      checkActive(controller.signal);
      if (!blob || !Number.isFinite(blob.size) || blob.size <= 0) throw new Error('The bed image is empty.');
      return blob;
    } finally {
      controller.abort();
      signal?.removeEventListener('abort', onAbort);
      images.forEach(image => image.removeAttribute('src'));
      wrapper.remove();
      cloneFrames.forEach(frame => frame.remove());
    }
  }

  function submit(snapshot, blob, { form, fileInput, variantInput, submitButton } = {}) {
    if (!snapshot?.properties || !Number.isFinite(snapshot.variantId) || !blob || !Number.isFinite(blob.size) || blob.size <= 0 ||
        !form || !fileInput || !variantInput || typeof submitButton?.click !== 'function' ||
        typeof File !== 'function' || typeof DataTransfer !== 'function') {
      throw new Error('The bed cart form is unavailable.');
    }
    if (submissions.has(form)) return submissions.get(form);
    const submissionError = (message, reviewRequired) => Object.assign(new Error(message), {
      code: 'submit', reviewRequired,
    });
    const doc = form.ownerDocument || document;
    const fragment = doc.createDocumentFragment();
    Object.entries(snapshot.properties).forEach(([key, value]) => {
      const input = doc.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value;
      input.setAttribute('data-bed-cart-property', '');
      fragment.appendChild(input);
    });
    const file = new File([blob], 'create-bed.jpg', { type: 'image/jpeg' });
    const transfer = new DataTransfer();
    transfer.items.add(file);
    fileInput.files = transfer.files;
    variantInput.value = snapshot.variantId;
    form.querySelectorAll('[data-bed-cart-property]').forEach(input => input.remove());
    form.appendChild(fragment);

    // These checks precede the click, so retrying cannot duplicate a request.
    if (submitButton.disabled || (typeof form.checkValidity === 'function' && !form.checkValidity())) {
      form.reportValidity?.();
      throw submissionError('The bed cart form cannot be submitted yet.', false);
    }

    let resolve, reject, submittedEvent, settled = false;
    const pending = new Promise((yes, no) => { resolve = yes; reject = no; });
    submissions.set(form, pending);
    const onSubmit = event => { submittedEvent = event; };
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      form.removeEventListener('submit', onSubmit, true);
      window.removeEventListener('pagehide', onPageHide);
      if (error) reject(error);
      else resolve();
    };
    const onPageHide = () => finish();
    // A completed click does not confirm Shopify accepted the multipart form.
    // Keep this attempt locked if navigation stalls: review the cart before
    // trying again, since a request may have succeeded without its response.
    const timeout = setTimeout(() => finish(submissionError('The bed cart submission did not navigate.', true)), 10000);
    form.addEventListener('submit', onSubmit, true);
    window.addEventListener('pagehide', onPageHide);
    try {
      submitButton.click();
      if (!settled && (!submittedEvent || submittedEvent.defaultPrevented)) {
        // Even a cancelled native submit can have been handled by AJAX code.
        finish(submissionError('The bed cart submission was interrupted.', true));
      }
    } catch (error) {
      finish(submissionError('The bed cart submission was interrupted.', true));
    }
    return pending;
  }

  window.MaisonBedCart = { createSnapshot, capture, submit };
})();
